import React, { useState } from 'react';
import { Modal, Button, Table, message, Space, Alert, Tag, Spin } from 'antd';
import { DownloadOutlined, FileTextOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const formatFtpDate = (value) => {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('DD/MM/YYYY HH:mm') : String(value);
};

const DBFImportModal = ({ visible, onClose, onImport, useListHook, useImportHook }) => {
  const navigate = useNavigate();
  const { fetchFiles, loading: loadingFiles } = useListHook();
  const { importData, loading: loadingImport } = useImportHook();
  
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importedRecords, setImportedRecords] = useState(null);
  const [step, setStep] = useState(1); // 1: select file, 2: preview records
  const distinctArticleCount = new Set((importedRecords?.records || []).map(row => row.CODE_ART)).size;

  const handleFetchFiles = async () => {
    try {
      const result = await fetchFiles();
      setFiles(result || []);
      if (result.length === 0) {
        message.warning('Aucun fichier DBF trouvé dans le répertoire FTP');
      }
    } catch (error) {
      message.error(`Erreur lors de la récupération des fichiers: ${error.message}`);
    }
  };

  const handleSelectFile = async (fileName) => {
    setSelectedFile(fileName);
    try {
      message.loading({ content: 'Importation du fichier DBF...', key: 'import' });
      const result = await importData(fileName);
      message.success({ content: `${result.processedRecords} lignes chargées avec succès`, key: 'import' });
      
      setImportedRecords({
        ...result,
        records: (result.records || []).map((row, index) => ({
          ...row,
          sourceLine: row.sourceLine ?? index + 1,
          _ftpRowKey: `ftp-row-${index}`,
        })),
      });
      setStep(2);
      
      if (result.missingArticles && result.missingArticles.length > 0) {
        message.warning(`${result.missingArticles.length} articles non trouvés dans la base de données`);
      }
    } catch (error) {
      message.error({ content: `Erreur lors de l'importation: ${error.message}`, key: 'import' });
    }
  };

  const handleConfirmImport = () => {
    if (importedRecords && importedRecords.records) {
      onImport(importedRecords.records);
      handleClose();
    }
  };

  const handleClose = () => {
    setFiles([]);
    setSelectedFile(null);
    setImportedRecords(null);
    setStep(1);
    onClose();
  };

  const fileColumns = [
    {
      title: 'Nom du fichier',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <Space>
          <FileTextOutlined />
          {text}
        </Space>
      ),
    },
    {
      title: 'Date FTP',
      dataIndex: 'modifiedAt',
      key: 'modifiedAt',
      width: 160,
      sorter: (a, b) => {
        const da = a.modifiedAt ? new Date(a.modifiedAt).getTime() : 0;
        const db = b.modifiedAt ? new Date(b.modifiedAt).getTime() : 0;
        return da - db;
      },
      defaultSortOrder: 'descend',
      render: (value) => formatFtpDate(value),
    },
    {
      title: 'Taille',
      dataIndex: 'size',
      key: 'size',
      width: 110,
      render: (size) => `${(size / 1024).toFixed(2)} KB`,
    },
    {
      title: 'Action',
      key: 'action',
      width: 110,
      render: (_, record) => (
        <Button 
          type="primary" 
          size="small"
          onClick={() => handleSelectFile(record.name)}
          loading={loadingImport && selectedFile === record.name}
        >
          Importer
        </Button>
      ),
    },
  ];

  const recordColumns = [
    { title: 'Ligne fichier', dataIndex: 'sourceLine', key: 'sourceLine', width: 100 },
    {
      title: 'Code Article',
      dataIndex: 'CODE_ART',
      key: 'CODE_ART',
      width: 120,
    },
    {
      title: 'Article',
      dataIndex: 'ARTICLE',
      key: 'ARTICLE',
      ellipsis: true,
    },
    {
      title: 'Quantité',
      dataIndex: 'QTE',
      key: 'QTE',
      width: 100,
      render: (val) => val?.toFixed(2),
    },
    {
      title: 'Carton',
      dataIndex: 'CARTON',
      key: 'CARTON',
      width: 80,
      render: (val) => val || 0,
    },
    {
      title: 'PA HT',
      dataIndex: 'PA_HT',
      key: 'PA_HT',
      width: 100,
      render: (val) => val?.toFixed(2),
    },
    {
      title: 'PA TTC',
      dataIndex: 'PA_TTC',
      key: 'PA_TTC',
      width: 100,
      render: (val) => val?.toFixed(2),
    },
    {
      title: 'TVA',
      dataIndex: 'TVA',
      key: 'TVA',
      width: 80,
      render: (val) => `${val}%`,
    },
    {
      title: 'Total HT',
      dataIndex: 'TotalHT',
      key: 'TotalHT',
      width: 100,
      render: (val) => val?.toFixed(2),
    },
    {
      title: 'Total TTC',
      dataIndex: 'TotalTTC',
      key: 'TotalTTC',
      width: 100,
      render: (val) => val?.toFixed(2),
    },
  ];

  React.useEffect(() => {
    if (visible && step === 1 && files.length === 0) {
      handleFetchFiles();
    }
  }, [visible]);

  return (
    <Modal
      title={step === 1 ? "Importer depuis FTP (DBF)" : "Prévisualisation des articles"}
      open={visible}
      onCancel={handleClose}
      width={step === 1 ? 820 : 1200}
      footer={
        step === 1 ? [
          <Button key="refresh" onClick={handleFetchFiles} loading={loadingFiles}>
            Actualiser
          </Button>,
          <Button key="cancel" onClick={handleClose}>
            Annuler
          </Button>,
        ] : [
          <Button key="back" onClick={() => setStep(1)}>
            Retour
          </Button>,
          <Button key="cancel" onClick={handleClose}>
            Annuler
          </Button>,
          <Button key="import" type="primary" onClick={handleConfirmImport}>
            Confirmer l'importation
          </Button>,
        ]
      }
    >
      {step === 1 ? (
        <div>
          <Alert
            message="Sélectionnez un fichier DBF depuis le serveur FTP"
            description="Les fichiers sont situés dans le répertoire /celer/achat sur le serveur FTP"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          
          <Table
            columns={fileColumns}
            dataSource={files}
            rowKey="name"
            loading={loadingFiles}
            pagination={false}
            locale={{
              emptyText: loadingFiles ? <Spin /> : 'Aucun fichier trouvé'
            }}
          />
        </div>
      ) : (
        <div>
          <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
            <Alert
              message={`${importedRecords?.records?.length || 0} lignes prêtes à être importées (${distinctArticleCount} articles distincts)`}
              description={
                importedRecords?.missingArticles?.length > 0 
                  ? `Attention: ${importedRecords.missingArticles.length} articles n'ont pas été trouvés dans la base de données`
                  : 'Tous les articles ont été trouvés dans la base de données'
              }
              type={importedRecords?.missingArticles?.length > 0 ? 'warning' : 'success'}
              showIcon
            />
            
            {importedRecords?.missingArticles?.length > 0 && (
              <Alert
                message="Articles manquants"
                description={
                  <div>
                    <div>
                      {importedRecords.missingArticles.slice(0, 10).map(code => (
                        <Tag key={code} color="red">{code}</Tag>
                      ))}
                      {importedRecords.missingArticles.length > 10 && (
                        <span>... et {importedRecords.missingArticles.length - 10} autres</span>
                      )}
                    </div>
                    <Button
                      type="primary"
                      icon={<PlusCircleOutlined />}
                      style={{ marginTop: 12 }}
                      onClick={() => {
                        handleClose();
                        navigate('/article?importDbf=1');
                      }}
                    >
                      Créer les articles manquants
                    </Button>
                  </div>
                }
                type="error"
              />
            )}
          </Space>
          
          <Table
            columns={recordColumns}
            dataSource={importedRecords?.records || []}
            rowKey="_ftpRowKey"
            pagination={{ pageSize: 10 }}
            scroll={{ x: 'max-content' }}
            size="small"
          />
        </div>
      )}
    </Modal>
  );
};

export default DBFImportModal;

